import "../../IssuancePanel.css";
import "./IssuancePanelDynamic.css";

import { Dispatch, FormEvent, SetStateAction } from "react";

import { ProjectIssuanceConstants } from "../../../../constants";
import {
  PillData,
  ProjectIssuanceSpecificProperty,
  ProjectIssuanceSpecificSchema,
  SelectData,
} from "../../../../models";
import { roundToDecimalPlaces } from "../../../../utils";
import {
  ActionButtons,
  Column,
  DatePicker,
  DynamicFormFieldsFromJsonSchema,
  FormLabel,
  KanaTooltip,
  PillsList,
  ReadonlyText,
  Row,
  Select,
  TextInput,
} from "../../../../widget";
import { isUnitKmHabitatType } from "../../utils";
import {
  getConditionalConstValue,
  getConditionalEnumValues,
  getIssuanceFieldValue,
  getQuantityTypeLabel,
  isReadOnlyOrConstField,
  IssuancePanelData,
  normaliseFieldLabel,
  setIssuanceFieldValue,
  shouldRenderSchemaField,
} from "./utils";

interface IssuancePanelDynamicProps {
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined;

  issuanceData: IssuancePanelData;
  setIssuanceData: Dispatch<SetStateAction<IssuancePanelData>>;

  tags: PillData[];
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;

  isHandleSubmitLoading: boolean;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleClosePanel: () => void;
  verifierData: SelectData;
  isEditing: boolean;
}

export const IssuancePanelDynamic = ({
  issuanceSchema,
  issuanceData,
  setIssuanceData,
  tags,
  setShowTagsPanel,
  isHandleSubmitLoading,
  handleSubmit,
  handleClosePanel,
  verifierData,
  isEditing,
}: IssuancePanelDynamicProps): JSX.Element => {
  const schemaProperties = issuanceSchema?.properties ?? {};

  const quantityTypeDefinition = schemaProperties.quantityType;
  const quantityTypeOptions =
    quantityTypeDefinition &&
    !isReadOnlyOrConstField(quantityTypeDefinition) &&
    quantityTypeDefinition.enum &&
    quantityTypeDefinition.enum.length > 0
      ? quantityTypeDefinition.enum.map((optionValue) => ({ key: optionValue, value: optionValue }))
      : [];

  // BNG declares quantityType readOnly (the server derives it — BR-02855), so it offers no options
  // and renders read-only. Standards that offer a selectable enum (carbon/water/ecosystem uplift)
  // keep the editable picker.
  const isQuantityTypeReadOnly = quantityTypeOptions.length === 0;

  // Applied to both arms below, so the read-only column cannot disagree with the editable one.
  const quantityTypeLabel = getQuantityTypeLabel(issuanceSchema);

  /*
    Where the quantity-type column sits, from the schema's own property order: NN lists quantityType
    first, BNG and Carbon list it last. Anything that does not declare it keeps the previous
    behaviour of appending the column after the dynamic fields.
  */
  const quantityTypeColumnIndex = Object.keys(schemaProperties).indexOf("quantityType");

  const getFieldValue = (fieldKey: string): unknown => {
    return getIssuanceFieldValue(issuanceData, fieldKey);
  };

  // The BNG issuance schema pins quantityType per habitat group (BR-02855/BR-02856), so the
  // displayed unit type comes straight from the schema. Standards that declare no quantityType
  // condition fall back to whatever is stored.
  const readOnlyQuantityType =
    getConditionalConstValue(issuanceSchema, "quantityType", (field) => getIssuanceFieldValue(issuanceData, field)) ??
    issuanceData.quantityType;

  const setFieldValue = (fieldKey: string, value: unknown): void => {
    setIssuanceFieldValue(setIssuanceData, fieldKey, value);
    if (fieldKey === "habitatType") {
      // Re-read the schema with the incoming habitat group so the stored quantityType matches what
      // the panel now displays; the server re-derives it on save either way (KAN-9657).
      const derivedQuantityType = getConditionalConstValue(issuanceSchema, "quantityType", (field) =>
        field === fieldKey ? value : getIssuanceFieldValue(issuanceData, field)
      );

      setIssuanceData((previousData) => ({
        ...previousData,
        quantityType: derivedQuantityType ?? null,
      }));
      setIssuanceFieldValue(setIssuanceData, "habitat", null);
      if (isUnitKmHabitatType(value as string)) {
        setIssuanceFieldValue(setIssuanceData, "unitKm", getFieldValue("unitHa"));
        setIssuanceFieldValue(setIssuanceData, "unitHa", null);
      } else {
        setIssuanceFieldValue(setIssuanceData, "unitHa", getFieldValue("unitKm"));
        setIssuanceFieldValue(setIssuanceData, "unitKm", null);
      }
    }
  };

  const shouldRenderDynamicField = (fieldKey: string, fieldDefinition: ProjectIssuanceSpecificProperty): boolean => {
    if (!shouldRenderSchemaField(fieldKey, fieldDefinition)) {
      return false;
    }

    if (fieldKey !== "unitKm" && fieldKey !== "unitHa") {
      return true;
    }

    const habitatTypeValue = getFieldValue("habitatType");
    const habitatType = typeof habitatTypeValue === "string" ? habitatTypeValue : null;
    const shouldShowUnitKm = isUnitKmHabitatType(habitatType);

    return fieldKey === "unitKm" ? shouldShowUnitKm : !shouldShowUnitKm;
  };

  const isDynamicFieldReadonly = (fieldKey: string): boolean => {
    if (fieldKey !== "habitat") {
      return false;
    }

    const habitatGroupValue = getFieldValue("habitatType");
    if (habitatGroupValue == null) {
      return true;
    }

    return String(habitatGroupValue).trim() === "";
  };

  const renderVerifierField = (): JSX.Element => {
    if (verifierData && verifierData.length > 0) {
      return (
        <Select
          label="Validation & verification body (VVB)"
          data={verifierData}
          value={
            verifierData.some((element) => element.key === issuanceData.verifierUuid)
              ? issuanceData.verifierUuid
              : undefined
          }
          onChange={(verifierUuidNewValue) =>
            setIssuanceData({
              ...issuanceData,
              verifierUuid: verifierUuidNewValue,
            })
          }
        />
      );
    }

    return (
      <>
        <Row spacingV="m">
          <Column span={12}>
            <h3>Validation & verification body (VVB)</h3>
          </Column>
        </Row>
        <Row>
          <Column span={12}>
            <p className="body1 NoData">No validators available for this project</p>
          </Column>
        </Row>
      </>
    );
  };

  return (
    <div className="IssuancePanelContent">
      <form onSubmit={(e) => handleSubmit(e)}>
        {isEditing && (
          <Row spacingV="l">
            <Column span={12}>
              <p className="body1">
                Sold, reserved and quoted units are maintained by this issuance&apos;s transactions and cannot be edited
                here. here. To change the total, adjust available units.
              </p>
            </Column>
          </Row>
        )}
        <Row spacingV="l">
          <Column span={4}>
            <Select
              label="Issuance status"
              onChange={(value: string) =>
                setIssuanceData({
                  ...issuanceData,
                  status: value,
                })
              }
              value={issuanceData.status}
              data={ProjectIssuanceConstants.ISSUANCE_STATUS_SELECT_DATA}
              tooltip={
                <KanaTooltip
                  tooltipHeader=""
                  tooltipText="
                    <p><strong>Draft</strong> — model units you expect to generate but have not yet issued. Included in your Hub figures by default, hidden from the public directory.</p>
                    <p><strong>Issued</strong> — units formally issued. Shown on the public directory.</p>
                    <p><strong>Inactive</strong> — units that were never generated or did not materialise. Excluded from Hub figures by default and from all public counts.</p>"
                  allowDangerousHtml
                />
              }
            />
          </Column>
          {/* Only an issued issuance has an issuance date (BR-02286/02287). */}
          {issuanceData.status === ProjectIssuanceConstants.ISSUANCE_STATUS_ISSUED && (
            <Column offset="1_half" span={4}>
              <DatePicker
                label="Issuance date"
                onChange={(value) =>
                  setIssuanceData({
                    ...issuanceData,
                    issuanceDate: value ?? null,
                  })
                }
                value={issuanceData.issuanceDate ?? undefined}
                showFormat={false}
              />
            </Column>
          )}
        </Row>

        <Row spacingV="l">
          <Column span={4}>
            <TextInput
              label="Price"
              onChange={(value) =>
                setIssuanceData({
                  ...issuanceData,
                  price: value === "" ? null : Number(value),
                })
              }
              value={issuanceData.price?.toString()}
              name="price"
              type="number"
              minNumberValue={0}
              maxNumberValue={999999.99}
              prefix="£"
            />
          </Column>
          <Column offset="1_half" span={4}>
            <TextInput
              label="Available units"
              onChange={(value: string) =>
                setIssuanceData({
                  ...issuanceData,
                  availableQuantity: Number(value),
                })
              }
              value={issuanceData.availableQuantity?.toString()}
              name="availableUnits"
              type="number"
              minNumberValue={0}
              maxNumberValue={9999999999.99999}
            />
          </Column>
        </Row>

        {/* BR-02902: sold and reserved are maintained by the issuance's transactions and are not amendable
            after creation, so the edit panel shows them read-only rather than hiding them (REQ-002). */}
        <Row spacingV="l">
          <Column span={4}>
            {isEditing ? (
              <ReadonlyText
                label="Sold units"
                value={issuanceData.soldQuantity != null ? roundToDecimalPlaces(issuanceData.soldQuantity, 5) : "--"}
              />
            ) : (
              <TextInput
                label="Sold units"
                onChange={(value: string) =>
                  setIssuanceData({
                    ...issuanceData,
                    soldQuantity: Number(value),
                  })
                }
                value={issuanceData.soldQuantity?.toString()}
                name="soldUnits"
                type="number"
                minNumberValue={0}
                maxNumberValue={9999999999.99999}
              />
            )}
          </Column>
          <Column offset="1_half" span={4}>
            {isEditing ? (
              <ReadonlyText
                label="Reserved units"
                value={
                  issuanceData.reservedQuantity != null ? roundToDecimalPlaces(issuanceData.reservedQuantity, 5) : "--"
                }
              />
            ) : (
              <TextInput
                label="Reserved units"
                onChange={(value: string) =>
                  setIssuanceData({
                    ...issuanceData,
                    reservedQuantity: Number(value),
                  })
                }
                value={issuanceData.reservedQuantity?.toString()}
                name="reservedUnits"
                type="number"
                minNumberValue={0}
                maxNumberValue={9999999999.99999}
              />
            )}
          </Column>
        </Row>

        <Row spacingV="l">
          {/* A new issuance has no transactions yet, so quoted units would always read 0 — only the edit panel shows it. */}
          {isEditing && (
            <Column span={4}>
              <ReadonlyText
                label="Quoted units"
                value={
                  issuanceData.quotedQuantity != null ? roundToDecimalPlaces(issuanceData.quotedQuantity, 5) : "--"
                }
              />
            </Column>
          )}
          <Column offset={isEditing ? "1_half" : undefined} span={4}>
            <ReadonlyText
              label="Total units"
              value={
                issuanceData.soldQuantity != null ||
                issuanceData.reservedQuantity != null ||
                issuanceData.availableQuantity != null
                  ? roundToDecimalPlaces(
                      (issuanceData.soldQuantity ?? 0) +
                        (issuanceData.reservedQuantity ?? 0) +
                        (issuanceData.availableQuantity ?? 0),
                      5
                    )
                  : "--"
              }
            />
          </Column>
        </Row>

        <Row spacingV="l">
          <Column span={4}>
            <TextInput
              label="Serial number"
              onChange={(value) =>
                setIssuanceData({
                  ...issuanceData,
                  serialNumber: value,
                })
              }
              value={issuanceData.serialNumber}
              name="serialNumber"
            />
          </Column>
          <Column offset="1_half" span={4}>
            {renderVerifierField()}
          </Column>
        </Row>

        <DynamicFormFieldsFromJsonSchema
          schemaProperties={schemaProperties}
          getFieldValue={getFieldValue}
          onFieldChange={setFieldValue}
          shouldRenderField={shouldRenderDynamicField}
          isFieldReadonly={isDynamicFieldReadonly}
          getFieldLabel={(fieldKey) => normaliseFieldLabel(fieldKey)}
          getEnumValues={(fieldKey, fieldDefinition, getSchemaFieldValue) =>
            fieldDefinition.enum ?? getConditionalEnumValues(issuanceSchema, fieldKey, getSchemaFieldValue)
          }
          valueMappers={{
            text: ({ value }) => (value === "" ? null : value),
            number: ({ value }) => {
              if (value === "") {
                return null;
              }

              const parsedValue = Number(value);
              return Number.isNaN(parsedValue) ? null : parsedValue;
            },
            date: ({ value }) => value ?? null,
          }}
          getNumberInputProps={() => ({ minNumberValue: 0 })}
          renderLayout={(fields) => {
            /*
              The quantity-type and Tags columns are not dynamic fields - quantityType is excluded
              from the field loop as a common issuance key, and Tags is not a schema property at all
              - so both are laid out here alongside the fields rather than by the widget.

              quantityType takes the position its schema order gives it: first for Nutrient
              Neutrality, last for Biodiversity and Carbon, which is where it has always rendered.
              Tags stays last in every case. One list means the column offsets are computed once, so
              a field landing on the left or right of a row cannot depend on which branch built it.
            */
            const layoutCells: { key: string; isFullWidth?: boolean; element: JSX.Element }[] = fields.map((field) => ({
              ...field,
              element: <div>{field.element}</div>,
            }));

            const quantityTypeCell = {
              key: "quantityType",
              element: isQuantityTypeReadOnly ? (
                <ReadonlyText label={quantityTypeLabel} value={readOnlyQuantityType} />
              ) : (
                <Select
                  label={quantityTypeLabel}
                  onChange={(value: string) =>
                    setIssuanceData({
                      ...issuanceData,
                      quantityType: value,
                    })
                  }
                  value={issuanceData.quantityType}
                  data={quantityTypeOptions}
                />
              ),
            };

            // A schema that does not declare quantityType keeps the previous behaviour: appended.
            const quantityTypeInsertAt =
              quantityTypeColumnIndex < 0 ? layoutCells.length : Math.min(quantityTypeColumnIndex, layoutCells.length);
            layoutCells.splice(quantityTypeInsertAt, 0, quantityTypeCell);

            layoutCells.push({
              key: "tags",
              element: (
                <>
                  <FormLabel label="Tags" />
                  <PillsList pills={tags} linkText="+ Add a tag" linkOnClick={() => setShowTagsPanel(true)} />
                </>
              ),
            });

            const { columns } = layoutCells.reduce<{
              columns: JSX.Element[];
              columnSlot: number;
            }>(
              (acc, cell) => {
                if (cell.isFullWidth) {
                  acc.columns.push(
                    <Column span={12} key={cell.key} className="IssuancePanelDynamicJsonField">
                      {cell.element}
                    </Column>
                  );
                  acc.columnSlot = 0;
                } else {
                  acc.columns.push(
                    <Column
                      span={4}
                      offset={acc.columnSlot % 2 ? "1_half" : undefined}
                      key={cell.key}
                      className="IssuancePanelDynamicJsonField"
                    >
                      {cell.element}
                    </Column>
                  );
                  acc.columnSlot += 1;
                }
                return acc;
              },
              { columns: [], columnSlot: 0 }
            );

            return <Row spacingV="ml">{columns}</Row>;
          }}
        />

        <Row spacingV="4xl">
          <Column span={12}>
            <ActionButtons
              submitButtonLabel="Save changes"
              onCancel={handleClosePanel}
              isLoading={isHandleSubmitLoading}
            />
          </Column>
        </Row>
      </form>
    </div>
  );
};
